import { useEffect, useRef, useState } from "react";
import { datadogLogs } from "@datadog/browser-logs";
import { getDeviceContext } from "../Utils/deviceValidation.js";

/**
 * Passive, non-prompting check of whether the camera is currently usable: a
 * camera device exists AND permission for it hasn't been revoked. Used to
 * disable capture buttons as a second line of defense on top of the
 * page-level CameraGate (which already requested and confirmed access
 * before any of this can render) — this catches the camera being revoked
 * mid-session (e.g. from the browser's site settings) or unplugged,
 * without ever prompting on its own.
 *
 * `logContext` (optional) is merged into the Datadog log emitted whenever
 * readiness flips to false, e.g. `{ invitationId, component: "AutoCapture" }`.
 */
export const useCameraReady = (logContext) => {
    const [cameraReady, setCameraReady] = useState(true);
    const wasReadyRef = useRef(true);

    useEffect(() => {
        let cancelled = false;
        let permissionStatus;

        const hasVideoInputDevice = async () => {
            if (!navigator?.mediaDevices?.enumerateDevices) return true;
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                return devices.some((device) => device.kind === "videoinput");
            } catch (error) {
                return true;
            }
        };

        const evaluate = async () => {
            const hasCameraDevice = await hasVideoInputDevice();
            // Only treat permission as the blocker on an explicit "denied".
            // Several mobile browsers report "prompt" from this API even
            // after access was already granted via getUserMedia (the
            // camera/microphone query support is inconsistent across
            // engines), so requiring an exact "granted" match disabled
            // capture buttons that were actually working fine.
            const permissionOk =
                !permissionStatus || permissionStatus.state !== "denied";
            const nextReady = hasCameraDevice && permissionOk;

            if (wasReadyRef.current && !nextReady) {
                datadogLogs.logger.error(
                    `Capture button disabled - camera not ready (useCameraReady)`,
                    {
                        hasCameraDevice,
                        permissionState: permissionStatus?.state,
                        ...getDeviceContext(),
                        ...logContext,
                    }
                );
            }
            wasReadyRef.current = nextReady;

            if (!cancelled) setCameraReady(nextReady);
        };

        const handleChange = () => evaluate();

        const subscribe = async () => {
            try {
                if (navigator?.permissions?.query) {
                    permissionStatus = await navigator.permissions.query({
                        name: "camera",
                    });
                    permissionStatus.addEventListener("change", handleChange);
                }
            } catch (error) {
                // Permissions API for "camera" isn't supported everywhere
                // (e.g. Safari) — fails open, page-level CameraGate is the
                // authoritative check for that case.
            }

            navigator?.mediaDevices?.addEventListener?.(
                "devicechange",
                handleChange
            );
            evaluate();
        };

        subscribe();

        return () => {
            cancelled = true;
            permissionStatus?.removeEventListener("change", handleChange);
            navigator?.mediaDevices?.removeEventListener?.(
                "devicechange",
                handleChange
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return cameraReady;
};
